/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/HelpCenter.tsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {Helmet} from "react-helmet-async";
import { useDarkMode } from "../context/DarkModeContext";
import {
  Search,
  Phone,
  MessageCircle,
  Users,
  User,
  Briefcase,
  FileText,
  CreditCard,
  Settings,
  ArrowRight,
  BookOpen,
  HelpCircle,
} from "lucide-react";

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();

  const helpCategories = [
    {
      icon: User,
      title: "Account & Profile",
      description:
        "Manage your account settings, profile, and personal information",
      guides: [
        {
          title: "How to Create and Verify Your Account",
          duration: "5 min read",
        },
        { title: "Updating Your Profile Information", duration: "3 min read" },
        {
          title: "Managing Privacy and Security Settings",
          duration: "4 min read",
        },
        { title: "Connecting Social Media Accounts", duration: "2 min read" },
      ],
    },
    {
      icon: Briefcase,
      title: "Job Search",
      description:
        "Find and apply to jobs that match your skills and preferences",
      guides: [
        { title: "Advanced Job Search Techniques", duration: "6 min read" },
        { title: "Setting Up Job Alerts", duration: "3 min read" },
        { title: "Understanding Job Matching", duration: "4 min read" },
        { title: "Using Filters Effectively", duration: "3 min read" },
      ],
    },
    {
      icon: FileText,
      title: "Applications",
      description: "Create compelling applications and track your progress",
      guides: [
        { title: "Writing Effective Cover Letters", duration: "8 min read" },
        { title: "Tracking Application Status", duration: "3 min read" },
        { title: "Withdrawing Applications", duration: "2 min read" },
        { title: "Application Best Practices", duration: "5 min read" },
      ],
    },
    {
      icon: BookOpen,
      title: "Resume & Documents",
      description: "Upload, manage, and optimize your resume and documents",
      guides: [
        { title: "Creating an ATS-Friendly Resume", duration: "10 min read" },
        { title: "Uploading and Managing Documents", duration: "3 min read" },
        { title: "Resume Formatting Guidelines", duration: "5 min read" },
        { title: "Using Multiple Resumes", duration: "4 min read" },
      ],
    },
    {
      icon: CreditCard,
      title: "Subscription & Billing",
      description: "Manage your subscription plan and payment methods",
      guides: [
        { title: "Understanding Premium Features", duration: "5 min read" },
        { title: "Managing Your Subscription", duration: "3 min read" },
        { title: "Payment Methods and Billing", duration: "4 min read" },
        { title: "Cancellation and Refunds", duration: "3 min read" },
      ],
    },
    {
      icon: Settings,
      title: "Technical Support",
      description: "Troubleshoot technical issues and platform features",
      guides: [
        { title: "Browser Compatibility Guide", duration: "4 min read" },
        { title: "Mobile App Usage", duration: "3 min read" },
        { title: "Troubleshooting Common Issues", duration: "6 min read" },
        { title: "Platform Features Overview", duration: "5 min read" },
      ],
    },
  ];

  const popularArticles = [
    {
      title: "How to Optimize Your Profile for Better Job Matches",
      category: "Account & Profile",
      reads: "12.5K",
      featured: true,
    },
    {
      title: "Understanding Our Job Matching Algorithm",
      category: "Job Search",
      reads: "8.7K",
      featured: true,
    },
    {
      title: "Premium vs Free: Feature Comparison",
      category: "Subscription & Billing",
      reads: "15.2K",
      featured: true,
    },
    {
      title: "Resume Tips That Get You Noticed",
      category: "Resume & Documents",
      reads: "22.1K",
      featured: false,
    },
    {
      title: "How to Set Up Effective Job Alerts",
      category: "Job Search",
      reads: "7.3K",
      featured: false,
    },
    {
      title: "Managing Your Application Pipeline",
      category: "Applications",
      reads: "9.8K",
      featured: false,
    },
  ];

  const handleContactSupport = () => {
    navigate("/contact-support");
  };

  // const handleNavigateToFAQ = () => {
  //   navigate("/faq");
  // };

  const handleNavigateToCommunity = () => {
    navigate("/community");
  };

  const handleViewAllArticles = () => {
    navigate("/help/articles");
  };

  const handleArticleClick = (articleTitle: string) => {
    navigate(
      `/help/articles/${articleTitle.toLowerCase().replace(/\s+/g, "-")}`,
    );
  };

  const handleGuideClick = (guideTitle: string) => {
    navigate(`/help/guides/${guideTitle.toLowerCase().replace(/\s+/g, "-")}`);
  };

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? 'bg-gray-900 text-gray-100'
          : 'bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900'
      }`}
    >
      <Helmet>
        <title>Help Center - How to Use FindWork | FindWork Kenya</title>
        <meta
          name="description"
          content="Need help? Browse FindWork's help center for guides on finding jobs, managing your profile, payments, and subscriptions in Kenya."
        />
        <link rel="canonical" href="https://findwork.ltd/help-center" />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1
            className={`text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r ${
              isDark
                ? 'from-blue-400 to-blue-600 text-white'
                : 'from-sky-500 to-blue-600 text-transparent bg-clip-text'
            }`}
          >
            Help Center
          </h1>
          <p
            className={`text-lg max-w-2xl mx-auto mb-8 ${
              isDark ? 'text-gray-400' : 'text-blue-700'
            }`}
          >
            Find guides, tutorials, and resources to help you get the most out
            of FindWork
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help articles, guides, or topics..."
                className={`w-full px-6 py-4 pl-14 rounded-2xl border-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg ${
                  isDark
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400'
                    : 'bg-white border-blue-200 text-gray-900 placeholder-blue-400 shadow-lg shadow-blue-100'
                }`}
              />
              <div className="absolute left-5 top-1/2 transform -translate-y-1/2">
                <Search
                  className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-blue-500'}`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <button
            onClick={handleContactSupport}
            className={`p-6 rounded-xl text-left transition-all duration-200 group ${
              isDark
                ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                : 'bg-white border border-blue-200 shadow-lg shadow-blue-100 hover:shadow-xl hover:shadow-blue-200'
            }`}
          >
            <div
              className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                isDark
                  ? 'bg-blue-600'
                  : 'bg-gradient-to-r from-sky-500 to-blue-600'
              }`}
            >
              <Phone className="w-6 h-6 text-white" />
            </div>
            <h3
              className={`text-xl font-semibold mb-2 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              Contact Support
            </h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-blue-700'}`}>
              Get personalized help from our support team
            </p>
          </button>

          <Link
            to="/faq"
            className={`p-6 rounded-xl text-left transition-all duration-200 group block ${
              isDark
                ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                : 'bg-white border border-blue-200 shadow-lg shadow-blue-100 hover:shadow-xl hover:shadow-blue-200'
            }`}
          >
            <div
              className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                isDark
                  ? 'bg-blue-600'
                  : 'bg-gradient-to-r from-sky-500 to-blue-600'
              }`}
            >
              <HelpCircle className="w-6 h-6 text-white" />
            </div>
            <h3
              className={`text-xl font-semibold mb-2 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              FAQ
            </h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-blue-700'}`}>
              Find answers to frequently asked questions
            </p>
          </Link>

          <button
            onClick={handleNavigateToCommunity}
            className={`p-6 rounded-xl text-left transition-all duration-200 group ${
              isDark
                ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                : 'bg-white border border-blue-200 shadow-lg shadow-blue-100 hover:shadow-xl hover:shadow-blue-200'
            }`}
          >
            <div
              className={`w-12 h-12 rounded-lg mb-4 flex items-center justify-center ${
                isDark
                  ? 'bg-blue-600'
                  : 'bg-gradient-to-r from-sky-500 to-blue-600'
              }`}
            >
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3
              className={`text-xl font-semibold mb-2 ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              Community
            </h3>
            <p className={`${isDark ? 'text-gray-400' : 'text-blue-700'}`}>
              Connect with other FindWork users
            </p>
          </button>
        </div>

        {/* Popular Articles */}
        <div className="mb-12">
          <div className="flex justify-between items-center mb-8">
            <h2
              className={`text-2xl font-bold ${
                isDark ? 'text-white' : 'text-blue-800'
              }`}
            >
              Popular Articles
            </h2>
            <button
              onClick={handleViewAllArticles}
              className={`text-sm font-medium flex items-center space-x-1 ${
                isDark
                  ? 'text-blue-400 hover:text-blue-300'
                  : 'text-blue-600 hover:text-blue-800'
              }`}
            >
              <span>View all articles</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {popularArticles.map((article, index) => (
              <div
                key={index}
                onClick={() => handleArticleClick(article.title)}
                className={`rounded-xl p-6 transition-all duration-200 group cursor-pointer ${
                  article.featured
                    ? isDark
                      ? 'bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-700/50'
                      : 'bg-gradient-to-br from-sky-50 to-blue-100 border border-blue-200 shadow-lg'
                    : isDark
                      ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                      : 'bg-white border border-blue-200 shadow-md hover:shadow-lg'
                }`}
              >
                {article.featured && (
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 flex items-center space-x-1 ${
                      isDark
                        ? 'bg-blue-600 text-white'
                        : 'bg-blue-100 text-blue-600'
                    }`}
                  >
                    <span>⭐</span>
                    <span>Featured</span>
                  </span>
                )}
                <h3
                  className={`font-semibold mb-3 group-hover:underline ${
                    isDark ? 'text-white' : 'text-blue-800'
                  }`}
                >
                  {article.title}
                </h3>
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm ${
                      isDark ? 'text-gray-400' : 'text-blue-600'
                    }`}
                  >
                    {article.category}
                  </span>
                  <span
                    className={`text-sm flex items-center space-x-1 ${
                      isDark ? 'text-gray-500' : 'text-blue-500'
                    }`}
                  >
                    <BookOpen className="w-3 h-3" />
                    <span>{article.reads} reads</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Help Categories */}
        <div className="mb-12">
          <h2
            className={`text-2xl font-bold mb-8 ${
              isDark ? 'text-white' : 'text-blue-800'
            }`}
          >
            Browse by Category
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {helpCategories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <div
                  key={index}
                  className={`rounded-xl p-6 transition-all duration-200 group ${
                    isDark
                      ? 'bg-gray-800 border border-gray-700 hover:bg-gray-750'
                      : 'bg-white border border-blue-200 shadow-lg hover:shadow-xl hover:shadow-blue-200'
                  }`}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div
                      className={`p-3 rounded-lg ${
                        isDark ? 'bg-blue-600/20' : 'bg-blue-100'
                      }`}
                    >
                      <IconComponent
                        className={`w-6 h-6 ${
                          isDark ? 'text-blue-400' : 'text-blue-600'
                        }`}
                      />
                    </div>
                    <div>
                      <h3
                        className={`text-xl font-semibold mb-2 ${
                          isDark ? 'text-white' : 'text-blue-800'
                        }`}
                      >
                        {category.title}
                      </h3>
                      <p
                        className={`text-sm ${
                          isDark ? 'text-gray-400' : 'text-blue-700'
                        }`}
                      >
                        {category.description}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {category.guides.map((guide, guideIndex) => (
                      <div
                        key={guideIndex}
                        onClick={() => handleGuideClick(guide.title)}
                        className={`p-3 rounded-lg transition-colors cursor-pointer ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <span
                            className={`font-medium text-sm ${
                              isDark ? 'text-gray-300' : 'text-blue-800'
                            }`}
                          >
                            {guide.title}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded flex items-center space-x-1 ${
                              isDark
                                ? 'bg-gray-700 text-gray-400'
                                : 'bg-blue-100 text-blue-600'
                            }`}
                          >
                            <BookOpen className="w-3 h-3" />
                            <span>{guide.duration}</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Link
                    to={`/guides/category/${category.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full mt-4 py-2 text-sm font-medium rounded-lg transition-colors block text-center flex items-center justify-center space-x-1 ${
                      isDark
                        ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/20'
                        : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                    }`}
                  >
                    <span>View all guides</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* Support Banner */}
        <div
          className={`rounded-2xl p-8 text-center ${
            isDark
              ? 'bg-gradient-to-r from-blue-900/30 to-blue-800/20 border border-blue-700/50'
              : 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-xl'
          }`}
        >
          <h2
            className={`text-2xl font-bold mb-4 ${
              isDark ? 'text-white' : 'text-white'
            }`}
          >
            Still need help?
          </h2>
          <p
            className={`text-lg mb-6 max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-blue-100'
            }`}
          >
            Our support team is available 24/7 to help you with any questions or
            issues.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleContactSupport}
              className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                isDark
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-white text-blue-600 hover:bg-blue-50'
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              <span>Contact Support</span>
            </button>
            <Link
              to="/faq"
              className={`px-8 py-3 rounded-lg font-semibold border transition-all duration-200 text-center flex items-center justify-center space-x-2 ${
                isDark
                  ? 'border-blue-500 text-blue-400 hover:bg-blue-900/20'
                  : 'border-white text-white hover:bg-blue-700'
              }`}
            >
              <HelpCircle className="w-5 h-5" />
              <span>Browse FAQ</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
